import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useRouter } from 'next/router';
import { useUpdateBoatMutation } from 'components/blw/hooks';
import { useShowBannerOnAvailabilityToggle } from 'components/blw/banner';
import { Boat } from 'swagger/models';
import { useCloseModal } from 'components/remodal/v2/hooks';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import Button from 'components/ui/Button/Button';
import styled from 'styled-components';
import { mediaMdMax } from 'helpers/breakpoints';
import { Flex } from 'components/primitives/flex';
import boatsetterPaths from 'utils/boatsetterPaths';
import SelectInput from '../forms/SelectInput';
import { deniedStates } from '../manageBoat/utils';
import { useDeleteBoatMutation } from '../manage_boats/hooks';

const BoatDeleteModal = ({ boat: { id: boatId, state } }: { boat: Boat }) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const closeModal = useCloseModal();

  const { enqueueSnackbar } = useLazySnackbar();

  const [step, setStep] = useState(0);

  const methods = useForm({
    mode: 'onChange',
  });
  const { handleSubmit, watch } = methods;

  const showBannerOnAvailabilityToggle = useShowBannerOnAvailabilityToggle();

  const updateBoatMutation = useUpdateBoatMutation();
  const deleteBoatMutation = useDeleteBoatMutation();

  const loading = deleteBoatMutation.isLoading || updateBoatMutation.isLoading;

  const setInactive = async () => {
    await updateBoatMutation.mutateAsync({
      boatId,
      body: {
        appears_on_site: false,
      },
    });
    showBannerOnAvailabilityToggle(false);
    closeModal();
  };

  const deleteBoatAction = async () => {
    const reason = watch('reason');
    await deleteBoatMutation.mutateAsync({
      boatId,
      body: {
        reason,
      },
    });
    closeModal();
    await router.push(boatsetterPaths.myBoats);
    await queryClient.invalidateQueries(['myBoats']);
    enqueueSnackbar('Your boat has been deleted', { variant: 'success', autoHideDuration: 5000 });
  };

  const completeListing = () => {
    window.location.href = `/l/boats/${boatId}/edit`;
  };

  return (
    <Wrapper>
      <h5 style={{ width: '95%' }} className="u-textSemiBold u-mb1_5">
        Delete boat
      </h5>
      {step === 0 && (
        <>
          <p>
            {state &&
              state === 'incomplete' &&
              `Are you sure you want to delete your boat? You cannot undo this action.`}
            {state && deniedStates.includes(state) && `Are you sure you want to delete your boat?`}
            {!state &&
              `Are you sure you want to delete your boat? You cannot undo this action. You can always temporarily make your boat inactive instead.
             This removes your boat from search and disables renters from booking until you’re ready to rent it out again.`}
          </p>
          <Flex gap="20px" className="u-flex-linear u-mt2 u-mb2">
            <Button
              variant="danger"
              onClick={() => setStep(1)}
              type="button"
              disabled={loading}
              text="YES, I WANT TO DELETE MY BOAT"
            />
            {!state && (
              <Button
                onClick={setInactive}
                type="button"
                loading={loading}
                text="SET MY BOAT TO INACTIVE"
                variant="owner"
                outline
              />
            )}
            {state && state === 'incomplete' && (
              <div className="incomplete">
                <Button
                  className="u-mobile-fit"
                  onClick={completeListing}
                  outline
                  text="complete listing"
                  variant="owner"
                />
              </div>
            )}
          </Flex>
        </>
      )}
      {step === 1 && (
        <div>
          <p>
            <b>Why are you deleting this boat?</b>
          </p>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(deleteBoatAction)}>
              <SelectInput
                name="reason"
                defaultValue={null}
                showError
                defaultLabel="Select reason"
                options={[
                  { label: 'I sold my boat', value: 'I sold my boat' },
                  { label: 'I moved', value: 'I moved' },
                  { label: 'Boatsetter fees/Take rate', value: 'Boatsetter fees/Take rate' },
                  { label: 'I’m using a different platform', value: 'I’m using a different platform' },
                  { label: 'Not worth it', value: 'Not worth it' },
                  { label: 'Test listing', value: 'Test listing' },
                  { label: 'Other', value: 'Other' },
                ]}
                placeholder="Select reason"
                errorMessage="Reason is required"
                searchable={false}
              />
              {/* {errors.reason} */}
              <Flex gap="20px" className="u-flex-linear u-mt2 u-mb2">
                <Button type="submit" loading={loading} text="DELETE MY BOAT" disabled={loading} variant="danger" />
                <Button
                  disabled={loading}
                  onClick={closeModal}
                  type="button"
                  text="Nevermind"
                  variant="owner"
                  outline
                />
              </Flex>
            </form>
          </FormProvider>
        </div>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  ${mediaMdMax} {
    .u-flex {
      flex-wrap: wrap;

      .u-ml2 {
        margin-left: 0;
      }

      .incomplete {
        width: 100%;
      }

      button {
        width: 100% !important;
        margin-bottom: 20px;
      }
    }
  }
`;

export default BoatDeleteModal;
